import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Description,
  Save,
  Edit,
  Preview,
  EventAvailable,
  Translate as TranslateIcon,
} from '@mui/icons-material';
import { bookingTermsService, globalService } from '../../services/firebase';
import { BookingTermsData, TranslatableField } from '../../types';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../../utils/getTranslatedData';
import ReactMarkdown from 'react-markdown';
import { produce } from 'immer';

const ManageBookingTermsPage: React.FC = () => {
  const [termsData, setTermsData] = useState<BookingTermsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedLang, setSelectedLang] = useState('tr');
  const { t, i18n } = useTranslation();

  const [editing, setEditing] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [originalTermsData, setOriginalTermsData] = useState<BookingTermsData | null>(null);

  useEffect(() => {
    const fetchTermsData = async () => {
      setLoading(true);
      try {
        let data = await bookingTermsService.getBookingTermsData();
        if (!data) {
          await bookingTermsService.initializeBookingTermsData();
          data = await bookingTermsService.getBookingTermsData();
        }
        setTermsData(data);
        setOriginalTermsData(JSON.parse(JSON.stringify(data)));
      } catch (err: any) {
        setError(t('booking_terms_admin.errors.load', { message: err.message }));
      } finally {
        setLoading(false);
      }
    };
    fetchTermsData();
  }, [t]);

  const handleInputChange = (field: keyof BookingTermsData, value: string) => {
    setTermsData(produce(draft => {
        if (!draft) return;
        let translatableField = draft[field] as TranslatableField;
        if (typeof translatableField !== 'object' || translatableField === null) {
            translatableField = { tr: '', en: '', ar: '' };
        }
        translatableField[selectedLang] = value;
        (draft as any)[field] = translatableField;
    }));
  };

  const handleTranslateAll = async () => {
      if (!termsData || selectedLang !== 'tr') return;
      setIsTranslating(true);
      setError(null);

      try {
        const titleTr = getTranslatedData(termsData.title, 'tr');
        const descriptionTr = getTranslatedData(termsData.description, 'tr');
        const contentTr = getTranslatedData(termsData.content, 'tr');

        const fieldsToTranslate = [
          { name: 'title', text: titleTr },
          { name: 'description', text: descriptionTr },
          { name: 'content', text: contentTr }
        ];
        
        const translationPromises = fieldsToTranslate
          .filter(field => field.text)
          .map(field => globalService.translateTexts({ text: field.text, sourceLang: 'tr', targetLangs: ['en', 'ar'] })
            .then(translations => ({ name: field.name, translations }))
          );

        const translatedFields = await Promise.all(translationPromises);
        
        setTermsData(produce(draft => {
            if (!draft) return;
            translatedFields.forEach(({ name, translations }) => {
              (draft as any)[name] = {
                  tr: getTranslatedData((draft as any)[name], 'tr'),
                  en: translations.en,
                  ar: translations.ar
              };
            });
        }));

        setSuccess(t('translationSuccess'));
      } catch (err: any) {
          setError(t('translationError', { message: err.message }));
      } finally {
          setIsTranslating(false);
      }
  };

  const handleSave = async () => {
    if (!termsData) return;
    if (!getTranslatedData(termsData.title, 'tr')?.trim() || !getTranslatedData(termsData.content, 'tr')?.trim()) {
      setError(t('booking_terms_admin.errors.required_tr'));
      return;
    }
    setIsSaving(true);
    try {
      await bookingTermsService.updateBookingTermsData(termsData);
      setOriginalTermsData(JSON.parse(JSON.stringify(termsData)));
      setEditing(false);
      setPreviewMode(false);
      setSuccess(t('booking_terms_admin.success.save'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(t('booking_terms_admin.errors.save', { message: err.message }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setTermsData(originalTermsData);
    setEditing(false);
    setPreviewMode(false);
  };

  const togglePreview = () => {
    setPreviewMode(!previewMode);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const formattedDate = termsData?.lastUpdated?.toDate 
    ? new Date(termsData.lastUpdated.toDate()).toLocaleString(i18n.language, { 
        day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
      })
    : t('common.notSpecified');

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
            <Typography variant="h4" component="h1" gutterBottom><Description sx={{ mr: 1, verticalAlign: 'middle' }} />{t('booking_terms_admin.title')}</Typography>
            <Typography variant="body1" color="text.secondary">{t('booking_terms_admin.description')}</Typography>
        </Box>
        {!editing && <Button onClick={() => setEditing(true)} startIcon={<Edit />} variant="outlined">{t('edit')}</Button>}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper sx={{ p: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <Chip icon={<EventAvailable />} label={`${t('booking_terms_admin.lastUpdated')}: ${formattedDate}`} />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <ToggleButtonGroup value={selectedLang} exclusive onChange={(_, lang) => lang && setSelectedLang(lang)} size="small" disabled={!editing}>
                    <ToggleButton value="tr">TR</ToggleButton>
                    <ToggleButton value="en">EN</ToggleButton>
                    <ToggleButton value="ar">AR</ToggleButton>
                </ToggleButtonGroup>
                {editing && (
                <>
                    <Tooltip title={t('translateAll')}>
                        <span>
                        <IconButton onClick={handleTranslateAll} disabled={isTranslating || selectedLang !== 'tr'}>
                            {isTranslating ? <CircularProgress size={24}/> : <TranslateIcon />}
                        </IconButton>
                        </span>
                    </Tooltip>
                    <Button onClick={togglePreview} startIcon={<Preview />} variant="outlined" size="medium">
                        {previewMode ? t('booking_terms_admin.form.editMode') : t('booking_terms_admin.form.previewMode')}
                    </Button>
                    <Button onClick={handleSave} startIcon={<Save />} variant="contained" disabled={isSaving}>
                        {isSaving ? <CircularProgress size={24} color="inherit" /> : t('save')}
                    </Button>
                    <Button onClick={handleCancel} color="secondary">{t('cancel')}</Button>
                </>
                )}
            </Box>
        </Stack>

        {editing ? (
          <Stack spacing={3}>
            <TextField label={t('booking_terms_admin.form.title')} value={getTranslatedData(termsData?.title, selectedLang)} onChange={(e) => handleInputChange('title', e.target.value)} fullWidth required />
            <TextField label={t('booking_terms_admin.form.description')} value={getTranslatedData(termsData?.description, selectedLang)} onChange={(e) => handleInputChange('description', e.target.value)} fullWidth multiline rows={2} />
            {previewMode ? (
              <Paper variant="outlined" sx={{ p: 2, minHeight: 400, bgcolor: 'grey.50' }}>
                <ReactMarkdown>{getTranslatedData(termsData?.content, selectedLang) || ''}</ReactMarkdown>
              </Paper>
            ) : (
              <TextField label={t('booking_terms_admin.form.content')} value={getTranslatedData(termsData?.content, selectedLang)} onChange={(e) => handleInputChange('content', e.target.value)} fullWidth multiline rows={15} required helperText={t('booking_terms_admin.form.contentHelper')} />
            )}
          </Stack>
        ) : (
          <Box sx={{py: 2}}>
            <Typography variant="h5" gutterBottom>{getTranslatedData(termsData?.title, i18n.language)}</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>{getTranslatedData(termsData?.description, i18n.language)}</Typography>
            <ReactMarkdown>{getTranslatedData(termsData?.content, i18n.language) || ''}</ReactMarkdown>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default ManageBookingTermsPage; 